<template>
  <div class="page-ai-playground">
    <div class="row gutter-md">
      <!-- Top Control Bar -->
      <div class="col-xs-12">
        <q-card>
          <q-card-main>
            <div class="row items-end gutter-sm">
              <div class="col-md-3 col-xs-12 q-mb-sm">
                <q-field stack-label="Agent">
                  <q-btn-group
                    outline
                  >
                    <q-btn
                      v-for="opt in agentOptions"
                      :key="opt.value"
                      size="md"
                      no-caps
                      :color="selectedAgent === opt.value ? 'primary' : ''"
                      :label="opt.label"
                      @click="onAgentSelect(opt.value)"
                    />
                  </q-btn-group>
                </q-field>
              </div>
              <div class="col-md-2 col-xs-12 q-mb-sm">
                <q-select
                  v-model="selectedPromptVersion"
                  :options="promptVersionOptions"
                  stack-label="Prompt Version"
                  @input="onPromptVersionChange"
                />
              </div>
              <div
                v-if="selectedAgent === 'scheduler'"
                class="col-md-2 col-xs-12 q-mb-sm"
              >
                <div class="row no-wrap items-end">
                  <div class="col">
                    <q-select
                      ref="inquirySelect"
                      v-model="selectedInquiry"
                      :options="inquiryOptions"
                      stack-label="Select Recent Inquiry"
                      @input="onInquirySelect"
                    />
                  </div>
                  <div class="q-ml-sm">
                    <q-btn
                      flat
                      dense
                      round
                      :loading="isReloadingInquiries"
                      :disable="isReloadingInquiries"
                      icon="refresh"
                      :title="isReloadingInquiries ? 'Refreshing…' : 'Refresh inquiries'"
                      @click.prevent="reloadInquiriesAndOpen"
                    />
                  </div>
                </div>
              </div>
              <template v-else-if="selectedAgent === 'receptionist'">
                <div class="col-md-2 col-xs-12 q-mb-sm">
                  <q-select
                    v-model="selectedBusiness"
                    :options="businessOptions"
                    stack-label="Business"
                    clearable
                    filter
                    emit-value
                    map-options
                    :loading="isLoadingBusinesses"
                    :disable="isLoadingBusinesses || businessOptions.length === 0"
                    @input="onBusinessSelect"
                  />
                </div>
                <div class="col-md-2 col-xs-12 q-mb-sm">
                  <div class="row no-wrap items-end">
                    <div class="col">
                      <q-field>
                        <autocomplete-chips
                          :key="selectedBusiness || 'no-business'"
                          method="autocompleteLead"
                          data-key="full_name"
                          placeholder="Leads"
                          :disable="!selectedBusiness"
                          :min-characters="1"
                          @change="leadAutocompleteChange"
                        />
                      </q-field>
                    </div>
                  </div>
                </div>
                <div class="col-md-2 col-xs-12 q-mb-sm">
                  <q-btn
                    dense
                    :disable="!selectedBusiness"
                    label="New lead"
                    no-caps
                    @click.prevent="startWithNewLead"
                  />
                  <sd-copy-to-clipboard
                    v-if="lead && lead.phone_number"
                    :copy="lead.phone_number"
                    :with-icon="true"
                    class="ml-3"
                  >
                    {{ lead.phone_number }}
                  </sd-copy-to-clipboard>
                </div>
              </template>
              <div
                v-else-if="selectedAgent === 'post-tour-agent'"
                class="col-md-2 col-xs-12 q-mb-sm"
              >
                <div class="row no-wrap items-end">
                  <div class="col">
                    <q-select
                      ref="tourSelect"
                      v-model="selectedTour"
                      :options="completedTourOptions"
                      stack-label="Select Completed Tour"
                      @input="onTourSelect"
                    />
                  </div>
                  <div class="q-ml-sm">
                    <q-btn
                      flat
                      dense
                      round
                      :loading="isReloadingTours"
                      :disable="isReloadingTours"
                      icon="refresh"
                      :title="isReloadingTours ? 'Refreshing…' : 'Refresh tours'"
                      @click.prevent="reloadToursAndOpen"
                    />
                  </div>
                </div>
              </div>
            </div>
          </q-card-main>
        </q-card>
      </div>

      <!-- Two-Panel View: Prompt (left) and Chat (right) -->
      <div
        v-if="isPromptViewVisible"
        class="col-md-6 col-xs-12"
      >
        <q-card class="full-height">
          <q-card-main class="system-prompt-main">
            <div
              v-if="systemPromptLoading"
              class="text-center q-pa-md"
            >
              <q-spinner-puff
                size="30px"
                color="primary"
              />
              <div class="q-mt-sm text-grey-6">
                Loading prompt...
              </div>
            </div>
            <div
              v-else-if="systemPrompt"
              class="system-prompt-wrapper"
            >
              <div
                class="system-prompt-text"
                v-html="systemPrompt"
              />
            </div>
            <div
              v-else
              class="text-grey-6 text-center q-pa-md"
            >
              No system prompt available
            </div>
          </q-card-main>
        </q-card>
      </div>

      <div
        v-if="isPromptViewVisible"
        class="col-md-6 col-xs-12"
      >
        <q-card
          id="chat-timeline"
          ref="chatTimeline"
          class="full-height column no-wrap"
        >
          <q-card-title class="row items-center justify-between">
            <div class="text-subtitle1">
              Chat
            </div>
            <q-btn
              color="negative"
              flat
              dense
              :loading="isResettingConversation"
              :disable="!lead || !lead.conversation || !lead.conversation.id || isResettingConversation"
              label="Reset Conversation"
              no-caps
              @click.prevent="resetConversation"
            />
          </q-card-title>
          <q-card-main class="relative-position scrollable-wrapper col">
            <div
              v-if="messagesLoading"
              class="text-center q-pa-md"
            >
              <q-spinner-puff
                size="50px"
                color="primary"
              />
              <div class="q-mt-md text-primary text-bold">
                Loading messages...
              </div>
            </div>
            <div
              v-else-if="messages.length === 0"
              class="text-center text-grey-6 q-pa-md"
            >
              No messages yet
            </div>
            <div
              v-for="(messagesByDay, date, index) in messagesByDays"
              v-else
              :key="index"
            >
              <q-chip
                dense
                color="blue"
                text-color="white"
                class="event-date-chip"
              >
                {{ date }}
              </q-chip>
              <div
                v-for="message in messagesByDay"
                :key="message.id"
                class="message-wrapper"
              >
                <div
                  v-if="message.direction === 'outgoing'"
                  class="outgoing-message-wrapper"
                >
                  <p style="color:#59B380">
                    {{ message.sending_agent || message.causer || 'Agent' }}
                  </p>
                  <p class="message-body">
                    {{ message.body }}
                  </p>
                  <span>{{ dateInHumanFormat(message.created_at) }}</span>
                </div>
                <div
                  v-if="message.direction === 'incoming'"
                  class="incoming-message-wrapper"
                >
                  <p style="color:#41A5F5">
                    {{ lead && lead.full_name }}
                  </p>
                  <p class="message-body">
                    {{ message.body }}
                  </p>
                  <span>{{ dateInHumanFormat(message.created_at) }}</span>
                </div>
              </div>
            </div>
          </q-card-main>
          <q-card-separator />
          <q-card-main class="row gutter-xs">
            <div class="col-11">
              <q-field>
                <q-input
                  v-model="textMessageToAgent"
                  type="textarea"
                  :rows="2"
                  placeholder="Type a message to the agent"
                />
              </q-field>
            </div>
            <div class="col-1">
              <q-btn
                :disabled="textMessageToAgent === '' || smsIsSending"
                :loading="smsIsSending"
                rounded
                size="md"
                label="Send"
                no-caps
                @click.prevent="sendTextToAgent()"
              />
            </div>
          </q-card-main>
        </q-card>
      </div>

      <!-- Placeholder -->
      <div
        v-if="!isPromptViewVisible"
        class="col-xs-12"
      >
        <q-card class="full-height">
          <q-card-main class="text-center q-pa-xl">
            <q-icon
              name="chat"
              size="80px"
              color="grey-5"
            />
            <h5 class="text-grey-6 q-mt-lg">
              Select agent, prompt version and lead, then click Set
            </h5>
          </q-card-main>
        </q-card>
      </div>
    </div>
  </div>
</template>

<script>
import AutocompleteChips from '@/components/autocomplete-chips.vue';
import SdCopyToClipboard from '@/components/common/SdCopyToClipboard.vue';
import TourStatusChip from '../../components/tour-status-chip';

export default {
  components: {
    TourStatusChip,
    AutocompleteChips,
    SdCopyToClipboard,
  },
  data() {
    return {
      // AI Controls
      selectedAgent: 'receptionist',
      agentOptions: [
        { label: 'Scheduler', value: 'scheduler' },
        { label: 'Receptionist', value: 'receptionist' },
        { label: 'Post-Tour Agent', value: 'post-tour-agent' },
      ],
      selectedPromptVersion: 'latest',
      promptVersionOptions: [
        { label: 'Latest', value: 'latest' },
        { label: 'Production', value: 'production' },
      ],
      selectedLead: null,
      systemPrompt: null,
      systemPromptLoading: false,

      // Lead data
      lead: null,
      inquiries: null,
      propertyTimezone: null,
      loading: false,

      // Messages
      messages: [],
      messagesByDays: {},
      messagesLoading: false,
      textMessageToAgent: '',
      smsIsSending: false,

      // Reset conversation state
      isResettingConversation: false,

      // UI state
      isPromptViewVisible: false,

      // Recent inquiries dropdown
      selectedInquiry: null,
      inquiryOptions: [],
      isReloadingInquiries: false,

      // Receptionist: business & lead
      selectedBusiness: null,
      businessOptions: [],
      isLoadingBusinesses: false,
      selectedLeadOption: null,
      newLeadPhoneNumber: null,

      // Post-tour: completed tours
      selectedTour: null,
      completedTourOptions: [],
      isReloadingTours: false,
    };
  },
  computed: {
  },
  mounted() {
    if (this.selectedAgent === 'scheduler') {
      this.fetchRecentInquiries();
    } else {
      this.onAgentChange();
    }
  },
  methods: {
    onAgentSelect(agent) {
      this.selectedAgent = agent;
      this.onAgentChange();
    },

    onAgentChange() {
      this.systemPrompt = null;
      this.lead = null;
      this.newLeadPhoneNumber = null;
      this.messages = [];
      this.isPromptViewVisible = false;
      if (this.selectedAgent === 'receptionist') {
        this.selectedBusiness = null;
        this.fetchBusinesses();
      } else if (this.selectedAgent === 'post-tour-agent') {
        this.fetchCompletedTours();
      }
    },

    onPromptVersionChange() {
      this.systemPrompt = null;
    },

    async onInquirySelect(inquiryOption) {
      if (!inquiryOption) {
        this.selectedInquiry = null;
        this.selectedLead = null;
        this.lead = null;
        this.messages = [];
        this.systemPrompt = null;
        this.isPromptViewVisible = false;
        return;
      }
      this.selectedInquiry = this.inquiryOptions.find((inquiry) => inquiry.id === inquiryOption);
      this.selectedLead = this.selectedInquiry.lead;
      this.propertyTimezone = (this.selectedInquiry.property && this.selectedInquiry.property.timezone) || null;
      this.systemPrompt = null;
      this.messages = [];
      this.isPromptViewVisible = true;
      // Load details and AI data immediately upon selection
      await this.fetchSystemPromptAndLead();
    },

    async fetchRecentInquiries() {
      try {
        const response = await this.api().request('GET', 'admin/inquiries', {}, {
          limit: 10,
          per_page: 10,
          'scopes[status][0]': 'pending',
          'scopes[status][1]': 'scheduled',
          'scopes[status][2]': 'in.progress',
        });
        const list = response && (response.data || response) || [];
        this.inquiryOptions = list
          .filter((inquiry) => inquiry.unit && (['scheduled', 'in.progress'].includes(inquiry.status) || inquiry.unit.last_showing_profile.type !== 'publish_listing'))
          .map((inquiry) => {
            const leadName = inquiry.lead && inquiry.lead.full_name ? inquiry.lead.full_name : 'Unknown Lead';
            const address = inquiry.property && inquiry.property.short_address ? inquiry.property.short_address : (inquiry.interested_in_property || '');
            const unitName = inquiry.unit && inquiry.unit.name ? inquiry.unit.name : '';
            const tz = inquiry.property && inquiry.property.timezone;
            const createdAt = this.$momentUtil(inquiry.created_at, tz).format('MM/DD/YY h:mmA');
            const parts = [leadName, address, unitName].filter(Boolean);
            const label = `#${inquiry.id} — ${parts.join(' — ')} — ${createdAt}`;
            return { label, value: inquiry.id, ...inquiry };
          });
      } catch (error) {
        this.uiNotify(this.getFirstError(error) || error.message || 'Error loading recent inquiries', 'negative');
      }
    },

    async reloadInquiriesAndOpen() {
      if (this.isReloadingInquiries) return;
      this.isReloadingInquiries = true;
      try {
        await this.fetchRecentInquiries();
        if (this.$refs && this.$refs.inquirySelect && typeof this.$refs.inquirySelect.showPopup === 'function') {
          this.$refs.inquirySelect.showPopup();
        } else if (this.$refs && this.$refs.inquirySelect && typeof this.$refs.inquirySelect.focus === 'function') {
          this.$refs.inquirySelect.focus();
        }
      } finally {
        this.isReloadingInquiries = false;
      }
    },

    onBusinessSelect(businessId) {
      if (!businessId) {
        this.selectedBusiness = null;
        this.onReceptionistContextChange();
        return;
      }
      this.selectedBusiness = businessId;
      this.onReceptionistContextChange();
    },

    async fetchBusinesses() {
      this.isLoadingBusinesses = true;
      try {
        const response = await this.api().request(
          'GET',
          'admin/businesses',
          {},
          {
            'scopes[active]': 'true',
            limit: 25,
            page: 1,
            per_page: 25,
          },
        );
        const list = (response && (response.data || response)) || [];
        this.businessOptions = (Array.isArray(list) ? list : []).map((b) => {
          const baseName = b.name
            || b.company_name
            || b.business_name;
          const label = baseName
            ? `${baseName} (#${b.id})`
            : (b.id ? `Business #${b.id}` : 'Unknown Business');
          return {
            label,
            value: b.id,
            ...b,
          };
        });
      } catch (error) {
        this.uiNotify(
          this.getFirstError(error)
            || error.message
            || 'Error loading businesses',
          'negative',
        );
        this.businessOptions = [];
      } finally {
        this.isLoadingBusinesses = false;
      }
    },

    onReceptionistContextChange() {
      this.selectedLeadOption = null;
      this.newLeadPhoneNumber = null;
      this.lead = null;
      this.messages = [];
      this.systemPrompt = null;
      this.isPromptViewVisible = false;
    },

    startWithNewLead() {
      if (!this.selectedBusiness) return;
      this.selectedLeadOption = null;
      this.newLeadPhoneNumber = null;
      this.systemPrompt = null;
      this.messages = [];
      this.isPromptViewVisible = true;
      this.fetchSystemPromptAndLead();
    },

    leadAutocompleteChange(items, navigateWithKeyboardArrows) {
      if (navigateWithKeyboardArrows) return;
      if (!items || items.length === 0) {
        this.selectedLeadOption = null;
        this.newLeadPhoneNumber = null;
        this.lead = null;
        this.messages = [];
        this.systemPrompt = null;
        this.isPromptViewVisible = false;
        return;
      }
      const lastItem = items[items.length - 1];
      this.selectedLeadOption = lastItem.data ? lastItem.data.id : null;
      this.systemPrompt = null;
      this.messages = [];
      this.isPromptViewVisible = true;
      this.fetchSystemPromptAndLead().then(() => {
        const ri = this.lead && (this.lead.recent_inquiry || this.lead.recentInquiry);
        this.propertyTimezone = (ri && ri.property && ri.property.timezone) || null;
      });
    },

    async fetchCompletedTours() {
      try {
        const response = await this.api().request(
          'GET',
          'tours',
          {},
          {
            page: 1,
            limit: 25,
            per_page: 25,
            status: ['completed'],
            is_showami: false,
          },
        );
        const raw = response || {};
        let list = [];

        if (Array.isArray(raw)) {
          list = raw;
        } else if (Array.isArray(raw.data)) {
          list = raw.data;
        } else if (raw.data && Array.isArray(raw.data.data)) {
          list = raw.data.data;
        } else if (Array.isArray(raw.results)) {
          list = raw.results;
        }

        const tours = list.slice(0, 20);
        this.completedTourOptions = tours.map((t) => {
          const sched = t.schedule || t;
          const siList = sched.schedule_inquiries || sched.scheduleInquiries || [];
          const firstSi = Array.isArray(siList) ? siList[0] : null;
          const inquiry = (firstSi && firstSi.inquiry) || firstSi;
          const lead = (inquiry && inquiry.lead) || inquiry;
          const leadName = (lead && lead.full_name) || 'Unknown';
          const prop = t.property || t;
          const addr = prop.short_address || '';
          const unit = t.unit || t;
          const unitName = (unit && unit.name) || '';
          const tz = (prop && prop.timezone) || null;
          const date = t.tour_at
            ? this.$momentUtil(t.tour_at, tz).format('MM/DD/YY h:mmA')
            : '';
          const label = `#${t.id} — ${[leadName, addr, unitName].filter(Boolean).join(' — ')}${
            date ? ` — ${date}` : ''}`;
          return { label, value: t.id, ...t };
        });
      } catch (error) {
        this.uiNotify(this.getFirstError(error) || error.message || 'Error loading completed tours', 'negative');
      }
    },

    async onTourSelect(tourId) {
      if (!tourId) {
        this.selectedTour = null;
        this.lead = null;
        this.messages = [];
        this.systemPrompt = null;
        this.isPromptViewVisible = false;
        return;
      }
      const tour = this.completedTourOptions.find((t) => t.id === tourId);
      this.propertyTimezone = (tour && tour.property && tour.property.timezone) || null;
      this.systemPrompt = null;
      this.messages = [];
      this.isPromptViewVisible = true;
      await this.fetchSystemPromptAndLead();
    },

    async reloadToursAndOpen() {
      if (this.isReloadingTours) return;
      this.isReloadingTours = true;
      try {
        await this.fetchCompletedTours();
        if (this.$refs && this.$refs.tourSelect && typeof this.$refs.tourSelect.showPopup === 'function') {
          this.$refs.tourSelect.showPopup();
        } else if (this.$refs && this.$refs.tourSelect && typeof this.$refs.tourSelect.focus === 'function') {
          this.$refs.tourSelect.focus();
        }
      } finally {
        this.isReloadingTours = false;
      }
    },

    async fetchSystemPromptAndLead() {
      const params = { agent: this.selectedAgent, prompt_version: this.selectedPromptVersion };
      if (this.selectedAgent === 'scheduler') {
        if (!this.selectedLead) return;
        params.lead_id = this.selectedLead.id;
      } else if (this.selectedAgent === 'receptionist') {
        if (!this.selectedBusiness) return;
        params.business_id = this.selectedBusiness;
        if (this.selectedLeadOption && this.selectedLeadOption !== 'new') params.lead_id = this.selectedLeadOption;
      } else if (this.selectedAgent === 'post-tour-agent') {
        if (!this.selectedTour) return;
        params.tour_id = this.selectedTour;
      } else {
        return;
      }

      this.systemPromptLoading = true;
      this.messagesLoading = true;
      try {
        const response = await this.api().request('GET', 'admin/ai-playground/prompts', {}, params);
        this.systemPrompt = response.prompt || response;
        this.lead = response.lead || null;
        const convoMessages = (this.lead && this.lead.conversation && Array.isArray(this.lead.conversation.messages))
          ? this.lead.conversation.messages
          : [];
        this.messages = convoMessages;
        this.messagesByDays = this.sortAndGroupMessagesByDay(this.messages);
        // When using "New lead" (receptionist without lead_id), show phone from response
        if (this.selectedAgent === 'receptionist' && !params.lead_id && this.lead) {
          this.newLeadPhoneNumber = this.lead.phone_number || null;
        } else if (this.selectedAgent === 'receptionist' && params.lead_id) {
          this.newLeadPhoneNumber = null;
        }
      } catch (error) {
        this.uiNotify(this.getFirstError(error) || error.message || 'Error loading system prompt', 'negative');
        this.systemPrompt = null;
        this.lead = null;
        this.messages = [];
        this.messagesByDays = {};
      } finally {
        this.systemPromptLoading = false;
        this.messagesLoading = false;
      }
    },

    sortAndGroupMessagesByDay(messages) {
      const groupedByDay = {};
      const sortedMessages = messages.sort((a, b) => (
        this.$momentUtil(a.created_at, this.propertyTimezone)
          .isSameOrAfter(this.$momentUtil(b.created_at, this.propertyTimezone))
          ? 1
          : -1
      ));
      sortedMessages.forEach((message) => {
        const dayKey = this.$momentUtil(message.created_at, this.propertyTimezone).format('MM/DD/YY');
        if (groupedByDay[dayKey]) {
          groupedByDay[dayKey].push(message);
        } else {
          groupedByDay[dayKey] = [message];
        }
      });
      return groupedByDay;
    },

    dateInHumanFormat(date) {
      return this.$momentUtil(date, this.propertyTimezone).toDisplayShortTimeFormat();
    },

    sendTextToAgent() {
      if (this.textMessageToAgent !== '' && this.smsIsSending === false) {
        const content = (this.textMessageToAgent || '').trim();
        if (content === '') return;

        // 1) Empty the input and show loader by disabling the send button
        this.textMessageToAgent = '';
        this.smsIsSending = true;

        // 2) Format message object and push optimistically
        const tempId = `temp-${Date.now()}`;
        const optimisticMessage = {
          id: tempId,
          direction: 'incoming',
          body: content,
          created_at: new Date().toISOString(),
        };
        this.messages.push(optimisticMessage);
        this.messagesByDays = this.sortAndGroupMessagesByDay(this.messages);

        this.api().request('POST', `admin/ai-playground/message/${this.lead.conversation.id}`, {
          message: content,
        })
          .then((response) => {
            const newMessage = (response && (response.data || response)) || null;
            if (newMessage) {
              // Always append server message; keep the optimistically pushed one
              this.messages.push(newMessage);
              this.messagesByDays = this.sortAndGroupMessagesByDay(this.messages);
            }
          })
          .catch((error) => {
            // Rollback optimistic message and restore input content
            const idx = this.messages.findIndex((m) => m.id === tempId);
            if (idx > -1) this.messages.splice(idx, 1);
            this.messagesByDays = this.sortAndGroupMessagesByDay(this.messages);
            this.textMessageToAgent = content;
            this.uiNotify(this.getFirstError(error) || error.message || 'Error occurred', 'negative');
          })
          .finally(() => {
            this.smsIsSending = false;
          });
      }
    },

    async resetConversation() {
      if (!this.lead || !this.lead.conversation || !this.lead.conversation.id) {
        this.uiNotify('No conversation to reset', 'warning');
        return;
      }

      if (this.isResettingConversation) return;
      this.isResettingConversation = true;
      try {
        await this.api().request('DELETE', `admin/ai-playground/conversations/${this.lead.conversation.id}`);
        await this.fetchSystemPromptAndLead();
        this.uiNotify('Conversation reset', 'positive');
      } catch (error) {
        this.uiNotify(this.getFirstError(error) || error.message || 'Failed to reset conversation', 'negative');
      } finally {
        this.isResettingConversation = false;
      }
    },
  },
};
</script>

<style lang="scss">
.page-ai-playground {
  .q-btn-round {
    height: auto;
    width: auto;
  }

  .scrollable-wrapper {
    overflow-y: scroll;
    max-height: 70vh;
  }

  .event-date-chip {
    margin: 0.5rem;
    padding: 0.25rem 0.5rem;
    position: relative;
    left: 45%;
  }

  .ai-controls-sidebar {
    .q-collapsible-inner {
      margin-bottom: 16px;
    }

    .q-list {
      max-height: 100vh;
      padding: 8px 8px 8px 16px;
    }

    .collapsible-header {
      font-weight: bold
    }
  }
  .system-prompt-wrapper {
    flex: 1;
    display: flex;
    min-height: 0; // allow child to scroll within flex parent
    .system-prompt-text {
      white-space: pre-wrap;
      font-size: 13px;
      overflow-y: auto;
      flex: 1;
      h1,h2,h3,ul,ol,p,li {
        margin: 0;
      }
      h1{
        font-size: 20px;
        font-weight: bold;
      }
      h2{
        font-size: 18px;
        font-weight: bold;
      }
      h3 {
        font-size: 16px;
        font-weight: bold;
      }
      strong {
        font-size: initial;
      }
    }
  }
  .system-prompt-main {
    height: 66vh;
    display: flex;
    flex-direction: column;
    padding-right: 8px; // give space for scrollbar
  }
  .lead-sidebar {
    .q-collapsible-inner {
      margin-bottom: 16px;
    }

    .q-list {
      max-height: 100vh;
      padding: 8px 8px 8px 16px;
    }

    .q-item-label {
      padding-left: 8px;
    }

    .q-item {
      padding-left: 0;
      padding-right: 0;
    }

    .q-collapsible-sub-item {
      padding-left: 0;
      padding-right: 8px;
    }

    .q-collapsible + .q-collapsible {
      padding-top: 8px;
    }

    .collapsible-header {
      font-weight: bold
    }

    .inquiries-list {
      padding-top: 21px;

      .inquiry-item .q-card-separator {
        margin-left: -16px;
        margin-right: -16px;
      }
    }

    .lead-notes{
      max-height: 150px;
      overflow-y: auto;
    }
  }

  #chat-timeline {
    padding: 0.5rem;

    .q-card-main:first-child {
      height: 66vh;
    }

    .message-wrapper {
      display: flex;
      flex-direction: column;
      margin: 0.75rem 0;

      .message-body {
        white-space: pre-line;
      }
      .outgoing-message-wrapper {
        background: #F0F0F0;
        border-radius: 1rem;
        display: flex;
        flex-direction: column;
        align-self: flex-start;
        padding: 0.5rem 1rem;
        width: 60%;

        p:first-child {
          margin-bottom: 0.25rem;
        }

        p:nth-child(2) {
          width: 90%;
        }

        p {
          margin-bottom: 0;
          font-size: 14px;
        }

        span {
          align-self: flex-end;
        }
      }

      .incoming-message-wrapper {
        background: #eef6fd;
        border-radius: 1rem;
        display: flex;
        flex-direction: column;
        align-self: flex-end;
        padding: 0.5rem 1rem;
        width: 60%;
        margin-bottom: 1rem;

        p:first-child {
          margin-bottom: 0.25rem;
        }

        p {
          margin-bottom: 0;
          font-size: 14px;
        }

        span {
          align-self: flex-end;
        }
      }
    }
  }

  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: #DFE5EB;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #cce6eb;
  }
}
</style>
